import {observer} from 'mobx-react';
import React, {useCallback, useEffect, useMemo} from 'react';
import theme from '../view/theme.module.scss';
import {Table, useWindowSize} from '@vidazoo/ui-kit';
import {useChartsListContext} from '../context/ChartsListContext';
import {ChartsTableHeader} from './ChartsTableHeader';
import ChartsTableRow from './ChartsTableRow';

const ROW_HEIGHT = 40;

function ChartsList(): React.JSX.Element {
    const {model, tableRowSelectionHook} = useChartsListContext();
    const {clearSelection} = tableRowSelectionHook;
    const {width} = useWindowSize();
    const scrollbarHeight = useMemo(() => {
        const probe = document.createElement('div');
        probe.style.cssText = 'position:absolute;left:-9999px;width:100px;height:100px;overflow:scroll';
        document.body.appendChild(probe);
        const size = probe.offsetHeight - probe.clientHeight;
        probe.remove();
        return size;
    }, []);
    const horizontalScrollbar = model.rowWidth > Math.min(width ?? window.innerWidth, document.documentElement.clientWidth) ? scrollbarHeight : 0;

    useEffect(() => {
        clearSelection();
        model.fetch();
        model.resetFilter();
    }, []);

    const renderTableRow = useCallback((itemIndex: number, viewportIndex: number, style: React.CSSProperties) => {
        const item = model.maskItems.at(itemIndex);

        return (
            <ChartsTableRow
                key={item ? item._id : itemIndex}
                columnsManager={model}
                tableRowSelectionHook={tableRowSelectionHook}
                itemIndex={itemIndex}
                viewportIndex={viewportIndex}
                style={style}/>
        );
    }, [model.maskItems, model.columns, model]);

    const renderTableHeader = useCallback(() => {
        return (
            <ChartsTableHeader/>
        );
    }, [model]);

    if (model.items.isLoading) return <div className={theme.message} role="status">Loading Charts<br/>Please wait...</div>;
    if (model.items.error) return <div className={theme.message} role="alert">Failed to fetch charts. <button type="button" onClick={() => model.fetch()}>Retry</button></div>;

    return <>
        {model.actionError && <div className={theme.error} role="alert">{model.actionError}</div>}
        {!model.maskItems.length && <div className={theme.empty} role="status">No charts found</div>}
        <Table
            className={theme.table}
            itemsCount={model.maskItems.length}
            rowHeight={ROW_HEIGHT}
            viewportHeight={model.maskItems.length * ROW_HEIGHT + 48 + horizontalScrollbar}
            renderRow={renderTableRow}
            renderHeader={renderTableHeader}
            columnsManager={model}
            overscanItems={5}
            useStickyScrollbar
        />
    </>;
}

export default observer(ChartsList);
